import { useCallback } from 'react'
import {
  useAddCompanyEmailMutation,
  useAddCompanyPhoneMutation,
  useCreateCompanyMutation,
  useCreateUserCarrierAssociationMutation,
  useGetCarrierCompaniesByUserQuery,
} from 'store/company/company.api'
import { useCreateCarrierLoadConfirmationMutation } from 'store/orders/orders.api'
import { useUpdateLoadConfirmationDetailsMutation } from 'store/load-confirmation/load-confirmation.api'
import { useUploadLoadConfirmationToOrderMutation } from 'store/task-automation/task-automation.api'
import { useLazyListInvoicedocumentsQuery } from 'store/invoices/invoices.api'
import { getLatestLoadConfirmationS3Path } from 'utils/load-confirmation-documents'
import { getUserId } from 'utils/common'
import { formatPhoneForSave } from 'utils/phone'
import {
  DISPATCH_CONTACT_NAME,
  parseCarrierAddress,
} from 'pages/address/address-details/carrier-details/carrier-contact.utils'
import { LoadConfirmationFormValues } from 'modules/load-confirmation'
import {
  mapStopsToOrderEvents,
  resolveSourceOrderId,
} from 'modules/load-confirmation'
import { TripPayload } from 'store/trips/trips.types'
import { Order } from 'store/orders/orders.types'

export type CarrierLcSaveResult = {
  carrierOrderId: number
  carrierCompanyId: string
  carrierName: string
  dispatchEmail: string
  loadNumber: string
  s3Path: string
  sourceOrderId: number
}

export function useSendToCarrier() {
  const userId = getUserId()
  const { data: carrierCompanies = [] } = useGetCarrierCompaniesByUserQuery(
    userId ? { userId } : { userId: '' },
    { skip: !userId }
  )

  const [createCompany] = useCreateCompanyMutation()
  const [createCarrierAssociation] = useCreateUserCarrierAssociationMutation()
  const [addEmail] = useAddCompanyEmailMutation()
  const [addPhone] = useAddCompanyPhoneMutation()
  const [createCarrierLoadConfirmation] = useCreateCarrierLoadConfirmationMutation()
  const [updateLoadConfirmationDetails] = useUpdateLoadConfirmationDetailsMutation()
  const [uploadLoadConfirmationToOrder] = useUploadLoadConfirmationToOrderMutation()
  const [fetchOrderDocuments] = useLazyListInvoicedocumentsQuery()

  const resolveOrCreateCarrierCompany = useCallback(
    async (formData: LoadConfirmationFormValues) => {
      if (!userId) throw new Error('You must be signed in.')

      const existing = carrierCompanies.find(
        (carrier) =>
          carrier.companyId === formData.selectedCarrierCompanyId ||
          carrier.companyName.trim().toLowerCase() ===
            (formData.carrierName ?? '').trim().toLowerCase()
      )

      if (existing) return existing.companyId

      const company = await createCompany({
        userId,
        companyName: (formData.carrierName ?? '').trim(),
        ...parseCarrierAddress(formData.carrierAddress ?? ''),
      }).unwrap()

      await createCarrierAssociation({
        userId,
        companyId: company.id,
        dispatchName: (formData.dispatchName ?? '').trim() || undefined,
      }).unwrap()

      const phoneEntries = [
        { value: formData.dispatchPhone ?? '', phoneType: 'Dispatch' as const },
        { value: formData.dispatchCell ?? '', phoneType: 'DispatchCell' as const },
        {
          value: formData.dispatchAfterHours ?? '',
          phoneType: 'DispatchAfterHoursPhone' as const,
        },
      ]

      await Promise.all([
        (formData.dispatchEmail ?? '').trim()
          ? addEmail({
              companyId: company.id,
              body: {
                email: (formData.dispatchEmail ?? '').trim(),
                emailType: 'NotificationEmail',
                userType: 'company',
                contactName: DISPATCH_CONTACT_NAME,
              },
            }).unwrap()
          : Promise.resolve(),
        ...phoneEntries.map((entry) => {
          const trimmedPhone = formatPhoneForSave(entry.value.trim())
          if (!trimmedPhone) return Promise.resolve()
          return addPhone({
            companyId: company.id,
            body: {
              phone: trimmedPhone,
              phoneType: entry.phoneType,
              userType: 'company',
              contactName: DISPATCH_CONTACT_NAME,
            },
          }).unwrap()
        }),
      ])

      return company.id
    },
    [addEmail, addPhone, carrierCompanies, createCarrierAssociation, createCompany, userId]
  )

  const saveFutureLoadDetails = useCallback(
    async (formData: LoadConfirmationFormValues) => {
      if (!userId) return

      const body: Record<string, string> = {}
      if (formData.saveMyCompanyForFuture) {
        Object.assign(body, {
          name: formData.myCompanyName ?? '',
          address: formData.myCompanyAddress ?? '',
          issuedBy: formData.issuedBy ?? '',
          dispatchEmail: formData.myCompanyEmail ?? '',
          phone: formData.myCompanyPhone ?? '',
          cell: formData.myCompanyCell ?? '',
          afterHoursPhone: formData.myCompanyAfterHoursPhone ?? '',
          invoicePodContactName: formData.invoicePodContactName ?? '',
          invoicePodEmail: formData.invoicePodEmail ?? '',
          invoicePodPhone: formData.invoicePodPhone ?? '',
        })
      }
      if (formData.saveNotesForFuture) {
        body.note = formData.notes ?? ''
      }
      if (Object.keys(body).length === 0) return
      await updateLoadConfirmationDetails({ userId, body }).unwrap()
    },
    [updateLoadConfirmationDetails, userId]
  )

  const sendToCarrier = useCallback(
    async (
      formData: LoadConfirmationFormValues,
      tripTasks: TripPayload[],
      orders: Order[],
      pdfBlob?: Blob
    ): Promise<CarrierLcSaveResult> => {
      if (!(formData.carrierName ?? '').trim()) {
        throw new Error('Carrier name is required.')
      }
      if (!formData.currency || !formData.baseRate) {
        throw new Error('Currency and base rate are required.')
      }

      const sourceOrderId = resolveSourceOrderId(tripTasks, orders)
      if (!sourceOrderId) {
        throw new Error('Could not determine the source order from selected tasks.')
      }

      const [, carrierCompanyId] = await Promise.all([
        saveFutureLoadDetails(formData),
        resolveOrCreateCarrierCompany(formData),
      ])
      const orderEvents = mapStopsToOrderEvents(formData)
      const loadNumber = `LCLC-${sourceOrderId}`

      const carrierOrder = await createCarrierLoadConfirmation({
        sourceOrderId,
        order: {
          status: 'NEW',
          loadNumber,
          companyId: carrierCompanyId,
          customerId: carrierCompanyId,
          customerName: (formData.carrierName ?? '').trim(),
          notificationEmail: formData.dispatchEmail || '',
          billingEmail: formData.dispatchEmail || '',
          accountPayableEmail: formData.dispatchEmail || '',
          currency: formData.currency as 'CAD' | 'USD',
          baseRate: parseFloat(formData.baseRate) || 0,
          detentionCharges: parseFloat(formData.detentionCharges || '0') || 0,
          layoverCharges: parseFloat(formData.layoverCharges || '0') || 0,
          fuelSurcharge: parseFloat(formData.fuelSurcharge || '0') || 0,
          otherCharges: parseFloat(formData.otherCharges || '0') || 0,
          notes: formData.notes || '',
          orderEvents,
        },
      }).unwrap()

      const carrierOrderId = carrierOrder.orderId

      if (pdfBlob) {
        const pdfFile = new File(
          [pdfBlob],
          `LC_${loadNumber}.pdf`,
          { type: 'application/pdf' }
        )
        await uploadLoadConfirmationToOrder({
          orderId: carrierOrderId,
          file: pdfFile,
        }).unwrap()
      }

      const documents = await fetchOrderDocuments({
        orderId: String(carrierOrderId),
      }).unwrap()

      const s3Path = getLatestLoadConfirmationS3Path(documents.LC)

      return {
        carrierOrderId,
        carrierCompanyId,
        carrierName: (formData.carrierName ?? '').trim(),
        dispatchEmail: (formData.dispatchEmail ?? '').trim(),
        loadNumber,
        s3Path,
        sourceOrderId,
      }
    },
    [
      createCarrierLoadConfirmation,
      fetchOrderDocuments,
      resolveOrCreateCarrierCompany,
      saveFutureLoadDetails,
      uploadLoadConfirmationToOrder,
    ]
  )

  return { sendToCarrier }
}
